import Cross from '../../assets/cross';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Drawer, message } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';
import * as React from 'react';
import { UploadChangeParam } from 'antd/lib/upload';
import withStyles from 'react-jss';
import Helper from '../../library/Helper';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import { excelUploadStyles } from '../address-book/excel-upload.styles';
import { useTranslation } from 'react-i18next';
import { ReduxStore } from 'reducers/redux.types';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import AsyncBatchModalService from '../../services/async-batch-modal.service';

interface ExcelUploadProps extends StylesProps<ReturnType<typeof excelUploadStyles>> {
    onClose: () => void;
    uiTheme: ThemeType;
    handleParsedData: (data: any[]) => void;
    parsedData: any[];
    headerMapping: Record<string, string>;
    header: string;
    apiCallFn: (data: any) => Promise<any>;
    downloadSample: () => Promise<any>;
}

const ExcelUpload = (props: ExcelUploadProps) => {
    const {
        classes,
        onClose,
        uiTheme,
        handleParsedData,
        parsedData,
        headerMapping,
        header = 'Upload in Progress',
        apiCallFn,
        downloadSample,
    } = props;

    const { t } = useTranslation();
    const [isDrawerVisible, setIsDrawerVisible] = React.useState<boolean>(true);

    const handleDrawerClose = () => {
        if (!AsyncBatchModalService.getInstance().isVisible()) {
            setIsDrawerVisible(false);
            onClose();
        }
    };

    const handleSubmit = () => {
        setIsDrawerVisible(false);
        AsyncBatchModalService.getInstance().showModal({
            heading: header,
            apiCallFn,
            data: parsedData,
            getRequestBody: (item: any) => (item),
            failureArrayKeysToShow: ['code', 'error_message'],
            concurrencyLimit: 3,
            isSingleApi: true,
            dataHeaders: Object.keys(headerMapping),
            onClose: () => {
                onClose();
            },
            onAllBatchesComplete: async () => {
                return Promise.resolve();
            },
        });
    };

    const renderButton = (theme: ThemeType) => {
        const btnClass = classes.newButton;
        const isUploadInProgress = AsyncBatchModalService.getInstance().isVisible();

        return (
            <>
                <Button
                    type="primary"
                    loading={false}
                    className={btnClass}
                    disabled={!parsedData.length || isUploadInProgress}
                    style={{
                        border: `1px solid ${!parsedData?.length || isUploadInProgress ? '#EDEDED'
                            : theme.primaryColor}`,
                    }}
                    onClick={() => {
                        if (isUploadInProgress) {
                            message.warning('Please let current upload to complete and close the modal first');
                            return;
                        }
                        handleSubmit();
                    }}
                >
                    {t('submit')}
                </Button>
                {isUploadInProgress && (
                    <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '8px' }}>
                        {t('Please let current upload process to complete before starting a new one')}
                    </div>
                )}
            </>
        );
    };

    const renderHeader = (theme: ThemeType) => {
        return (
            <div className={classes.header}>
                <div className={classes.addText}>
                    <Cross onClick={handleDrawerClose} alt="close" className={classes.closeIcon} />
                    <span>
                        {t('bulk_upload')}
                    </span>
                </div>
                {renderButton(theme)}
            </div>
        );
    };

    const onFileRecieved = async (file: any) => {
        const uploadArr: any[] = await Helper.handleXLSXFile(file, Object.keys(headerMapping), 0);
        handleParsedData(uploadArr);
    };

    const handleUploadChange = (info: UploadChangeParam) => {
        const { status } = info.file;
        if (status === 'done') {
            onFileRecieved(info.file.originFileObj);
        }
    };

    const customRequest = ({ onSuccess, file }: any) => {
        setTimeout(() => {
            onSuccess(null, file);
        }, 100);
    };

    const downloadSampleHandler = async () => {
        const fileBuffer = await downloadSample();

        const fileName = fileBuffer.filename;
        if (fileBuffer.isSuccess) {
            Helper.downloadFileData(
                fileBuffer.data,
                fileName || 'bulk_upload_sample.xlsx',
                true,
            );
            message.success('Downloaded Successfully');
        } else {
            message.error(fileBuffer.errorMessage);
        }
    };

    const renderUpload = () => {
        return (
            <div
                className={classes.uploadBox}
            >
                <Dragger
                    multiple={false}
                    accept=".xlsx, .xls, .csv"
                    name="file"
                    onChange={handleUploadChange}
                    customRequest={customRequest}
                >
                    <p className={classes.uploadText}>
                        <UploadOutlined className={classes.closeIcon} />
                        {t('add_text')}
                    </p>
                    <p className={classes.uploadHint}>
                        {t('drag_and_drop_text')}
                        or
                        <Button type="link">Browse</Button>
                        to choose file
                    </p>
                </Dragger>
            </div>
        );
    };

    const renderDownload = () => {
        return (
            <Button
                type="link"
                className={classes.downloadBtn}
                onClick={downloadSampleHandler}
            >
                <DownloadOutlined />
                {t('download_sample_file')}
            </Button>
        );
    };

    return (
        <>
            <Drawer
                visible={isDrawerVisible}
                width="35%"
                title={renderHeader(uiTheme)}
                onClose={handleDrawerClose}
                className={classes.main}
                closable={false}
            >
                {renderUpload()}
                {renderDownload()}
            </Drawer>
        </>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    return {
        uiTheme: state.uiTheme,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default withStyles(excelUploadStyles)(GenericHoc(hocConfig)(ExcelUpload));
